import {
    ActionIcon,
    Button,
    Divider,
    Group,
    Paper,
    Stack,
    Text,
    Textarea,
    Title,
} from '@mantine/core'
import {IconEdit, IconTrash} from '@tabler/icons-react'
import {useState} from 'react'

import {useEditRights} from './edit-rights-context'
import {formatRelativeTime} from './format-relative-time'
import {createJobComment, deleteJobComment, updateJobComment} from './job-comments-api'
import type {JobDetail, JobDetailComment} from './types'

type JobCommentsSectionProps = {
    job: JobDetail
    currentUserId?: string
    onJobUpdated: (job: JobDetail) => void
    jobDataLayer?: {getJob: (jobId: string) => Promise<JobDetail>}
}

export function JobCommentsSection({
    job,
    currentUserId,
    onJobUpdated,
    jobDataLayer,
}: JobCommentsSectionProps) {
    const {canEdit} = useEditRights()
    const [composerContent, setComposerContent] = useState('')
    const [composerError, setComposerError] = useState<string | null>(null)
    const [editingCommentId, setEditingCommentId] = useState<string | null>(null)
    const [editContent, setEditContent] = useState('')
    const [editError, setEditError] = useState<string | null>(null)
    const [creating, setCreating] = useState(false)
    const [updating, setUpdating] = useState(false)
    const [deletingCommentId, setDeletingCommentId] = useState<string | null>(null)

    function canEditComment(createdById: string): boolean {
        return currentUserId === createdById
    }

    async function refreshJob() {
        if (!jobDataLayer) {
            return
        }
        const updatedJob = await jobDataLayer.getJob(job.id)
        onJobUpdated(updatedJob)
    }

    async function handleCreateComment() {
        const content = composerContent.trim()
        if (!content) {
            setComposerError('Comment content cannot be empty')
            return
        }

        setCreating(true)
        setComposerError(null)
        try {
            await createJobComment(job.id, {content})
            setComposerContent('')
            await refreshJob()
        } finally {
            setCreating(false)
        }
    }

    function handleStartEdit(comment: JobDetailComment) {
        setEditingCommentId(comment.id)
        setEditContent(comment.content)
        setEditError(null)
    }

    function handleCancelEdit() {
        setEditingCommentId(null)
        setEditContent('')
        setEditError(null)
    }

    async function handleSaveEdit(commentId: string) {
        const content = editContent.trim()
        if (!content) {
            setEditError('Comment content cannot be empty')
            return
        }

        setUpdating(true)
        setEditError(null)
        try {
            await updateJobComment(job.id, commentId, {content})
            setEditingCommentId(null)
            setEditContent('')
            await refreshJob()
        } finally {
            setUpdating(false)
        }
    }

    async function handleDeleteComment(commentId: string) {
        if (!confirm('Are you sure you want to delete this comment?')) {
            return
        }

        setDeletingCommentId(commentId)
        try {
            await deleteJobComment(job.id, commentId)
            if (editingCommentId === commentId) {
                handleCancelEdit()
            }
            await refreshJob()
        } finally {
            setDeletingCommentId(null)
        }
    }

    return (
        <Paper withBorder p="md" radius="md">
            <Stack gap="md">
                <Title order={3}>Comments</Title>
                <Text size="sm" c="dimmed">
                    Shared with drivers on this job.
                </Text>

                {job.comments.length === 0 ? (
                    <Text size="sm" c="dimmed">
                        No comments yet. Add a comment to track job information.
                    </Text>
                ) : (
                    <Stack gap="sm">
                        {job.comments.map((comment, index) => (
                            <Stack key={comment.id} gap="xs">
                                {index > 0 ? <Divider /> : null}
                                <Group justify="space-between" align="flex-start">
                                    <Group gap="xs" align="baseline">
                                        <Text size="sm" fw={700}>
                                            {comment.createdByName}
                                        </Text>
                                        <Text size="sm" c="dimmed">
                                            {formatRelativeTime(comment.createdAt)}
                                        </Text>
                                    </Group>
                                    {canEdit &&
                                    canEditComment(comment.createdById) &&
                                    editingCommentId !== comment.id ? (
                                        <Group gap="xs">
                                            <ActionIcon
                                                size="sm"
                                                variant="subtle"
                                                aria-label="Edit comment"
                                                onClick={() => handleStartEdit(comment)}
                                            >
                                                <IconEdit size={16} />
                                            </ActionIcon>
                                            <ActionIcon
                                                size="sm"
                                                variant="subtle"
                                                color="red"
                                                aria-label="Delete comment"
                                                loading={deletingCommentId === comment.id}
                                                onClick={() => void handleDeleteComment(comment.id)}
                                            >
                                                <IconTrash size={16} />
                                            </ActionIcon>
                                        </Group>
                                    ) : null}
                                </Group>

                                {editingCommentId === comment.id ? (
                                    <Stack gap="xs">
                                        <Textarea
                                            aria-label="Edit comment"
                                            value={editContent}
                                            onChange={(event) =>
                                                setEditContent(event.currentTarget.value)
                                            }
                                            error={editError}
                                            minRows={3}
                                            autosize
                                        />
                                        <Group justify="flex-end" gap="sm">
                                            <Button
                                                variant="default"
                                                size="compact-sm"
                                                onClick={handleCancelEdit}
                                                disabled={updating}
                                            >
                                                Cancel
                                            </Button>
                                            <Button
                                                size="compact-sm"
                                                loading={updating}
                                                onClick={() => void handleSaveEdit(comment.id)}
                                            >
                                                Save
                                            </Button>
                                        </Group>
                                    </Stack>
                                ) : (
                                    <>
                                        <Text size="sm">{comment.content}</Text>
                                        {comment.updatedAt && comment.updatedById ? (
                                            <Text size="xs" c="dimmed">
                                                Edited {formatRelativeTime(comment.updatedAt)} by{' '}
                                                {comment.updatedByName}
                                            </Text>
                                        ) : null}
                                    </>
                                )}
                            </Stack>
                        ))}
                    </Stack>
                )}

                {canEdit ? <Divider /> : null}

                {canEdit ? (
                    <Stack gap="xs">
                        <Textarea
                            aria-label="Add comment"
                            placeholder="Add a comment..."
                            value={composerContent}
                            onChange={(event) => {
                                setComposerContent(event.currentTarget.value)
                                if (composerError) {
                                    setComposerError(null)
                                }
                            }}
                            error={composerError}
                            minRows={3}
                            autosize
                        />
                        <Group justify="flex-end">
                            <Button
                                onClick={() => void handleCreateComment()}
                                loading={creating}
                                disabled={!composerContent.trim()}
                            >
                                Add comment
                            </Button>
                        </Group>
                    </Stack>
                ) : null}
            </Stack>
        </Paper>
    )
}
